// Expone el estado de autenticación de React y coordina la restauración inicial de la sesión.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { BASE_BACKEND_URL } from './apiConfig'

import {
  authFetch,
  clearAccessToken,
  getAccessToken,
  refreshAccessToken,
  setAccessToken
} from './authClient'
import { subscribeAuthEvents } from './authChannel'
import { hasSessionUser, saveSessionCookies } from './sessionCookies'
import { GlobalContext } from '../global_context/GlobalContext'

const AuthContext = createContext({
  isAuthenticated: false,
  isBootstrapping: true,
  bootstrap: async () => {},
  applyLoginPayload: () => {},
  clearAuth: () => {}
})

export function AuthProvider({ children }) {
  const { syncUserRole } = useContext(GlobalContext)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isBootstrapping, setIsBootstrapping] = useState(true)

  const applyLoginPayload = useCallback((payload) => {
    if (payload?.token) {
      setAccessToken(payload.token)
      setIsAuthenticated(true)
    }
  }, [])

  const clearAuth = useCallback(() => {
    clearAccessToken()
    setIsAuthenticated(false)
  }, [])

  const bootstrap = useCallback(async () => {
    setIsBootstrapping(true)

    try {
      let token = getAccessToken()
      if (!token) {
        token = await refreshAccessToken({ redirectOnFailure: false })
      }

      if (!token) {
        setIsAuthenticated(false)
        return
      }

      const response = await authFetch(`${BASE_BACKEND_URL}/users/current`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      })
      if (response?.ok) {
        const data = await response.json()
        if (!hasSessionUser(data)) {
          clearAccessToken()
          setIsAuthenticated(false)
          return
        }

        saveSessionCookies(data)
        syncUserRole?.()
        setIsAuthenticated(true)
      } else if (response?.status === 304) {
        setIsAuthenticated(true)
      } else {
        clearAccessToken()
        setIsAuthenticated(false)
      }
    } catch {
      clearAccessToken()
      setIsAuthenticated(false)
    } finally {
      setIsBootstrapping(false)
    }
  }, [syncUserRole])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  useEffect(
    () =>
      subscribeAuthEvents((event) => {
        if (event?.type !== 'LOGOUT') return

        // BroadcastChannel no necesita acceso a navigate y mantiene el cierre
        // de sesión consistente en todas las pestañas del mismo origen.
        setIsAuthenticated(false)
        window.location.replace('/?logout=true')
      }),
    []
  )

  const value = useMemo(
    () => ({
      isAuthenticated,
      isBootstrapping,
      bootstrap,
      applyLoginPayload,
      clearAuth
    }),
    [isAuthenticated, isBootstrapping, bootstrap, applyLoginPayload, clearAuth]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
